import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";

export default function SignupPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const handleSignup = async (e) => {
    e.preventDefault();
    setError(null);
    
    // Debug: Log what we're sending
    console.log("Attempting signup with:", {
      email: formData.email,
      passwordLength: formData.password.length,
      username: formData.username
    });

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match!");
      return;
    }

    // Validate password length
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    // Validate email
    if (!formData.email || formData.email.trim() === "") {
      setError("Email is required");
      return;
    }

    setLoading(true);

    try {
      // Sign up with Supabase Auth
      const signUpData = {
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            username: formData.username,
          },
        },
      };

      console.log("Calling supabase.auth.signUp with:", signUpData);

      const { data, error: signUpError } = await supabase.auth.signUp(signUpData);

      console.log("Signup response:", { data, error: signUpError });

      if (signUpError) throw signUpError;

      // Success!
      console.log("Signup successful!");
      alert("✅ Account created successfully!");
      navigate("/login");
      
    } catch (error) {
      console.error("Signup error:", error);
      
      // Handle specific error cases
      if (error.message?.includes("already registered")) {
        setError("This email is already registered. Try logging in instead.");
      } else if (error.message?.includes("username")) {
        setError("This username is already taken. Please choose another.");
      } else {
        setError(error.message || "Failed to create account. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setError(null);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 gradient-bg text-white p-12 flex-col justify-between">
        <div>
          <Link to="/">
            <h1 className="text-5xl font-bold mb-4 cursor-pointer">PickSlate</h1>
          </Link>
          <p className="text-xl text-emerald-100">
            Join season-long sports prediction leagues and compete with friends
          </p>
        </div>

        <div className="space-y-6">
          {[
            { title: "Free to Join", desc: "No credit card required" },
            { title: "Season-Long Leagues", desc: "Compete over 2-16 weeks" },
            { title: "Multi-Sport Play", desc: "Pick winners across NFL, NBA, NHL & more" },
            { title: "Track Your Success", desc: "Real-time standings and stats" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-2xl">
                ✓
              </div>
              <div>
                <h3 className="font-bold">{item.title}</h3>
                <p className="text-emerald-100 text-sm">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-sm text-emerald-200">
          © 2026 PickSlate. All rights reserved.
        </div>
      </div>

      {/* Right side - Signup Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold text-gray-900 mb-3">
              Create Account
            </h2>
            <p className="text-gray-600">Start your first league in seconds</p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 bg-red-50 border-2 border-red-300 text-red-800 px-4 py-3 rounded-xl">
              <p className="font-semibold">⚠️ {error}</p>
            </div>
          )}

          {/* Debug Info (Remove this after testing) */}
          {/* {process.env.NODE_ENV === 'development' && (
            <div className="mb-4 bg-blue-50 border-2 border-blue-300 text-blue-800 px-4 py-3 rounded-xl text-xs">
              <p className="font-semibold">Debug Info:</p>
              <p>Email: {formData.email || '(empty)'}</p>
              <p>Password length: {formData.password?.length || 0}</p>
              <p>Username: {formData.username || '(empty)'}</p>
            </div>
          )} */}

          <form onSubmit={handleSignup} className="space-y-5">
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">
                Username
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={(e) => handleChange("username", e.target.value)}
                placeholder="fanatic123"
                required
                minLength={3}
                maxLength={20}
                pattern="[a-zA-Z0-9_]+"
                title="Username can only contain letters, numbers, and underscores"
                disabled={loading}
                autoComplete="username"
                className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-lg outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">
                3-20 characters, letters, numbers, and underscores only
              </p>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="you@example.com"
                required
                disabled={loading}
                autoComplete="email"
                className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-lg outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">
                Password
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={(e) => handleChange("password", e.target.value)}
                placeholder="••••••••"
                required
                minLength={8}
                disabled={loading}
                autoComplete="new-password"
                className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-lg outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">
                Confirm Password
              </label>
              <input
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={(e) =>
                  handleChange("confirmPassword", e.target.value)
                }
                placeholder="••••••••"
                required
                disabled={loading}
                autoComplete="new-password"
                className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition text-lg outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div className="flex items-start">
              <input
                type="checkbox"
                required
                disabled={loading}
                className="w-4 h-4 text-emerald-600 rounded focus:ring-2 focus:ring-emerald-500 mt-1 disabled:cursor-not-allowed"
              />
              <label className="ml-2 text-sm text-gray-600">
                I agree to the{" "}
                <a
                  href="#"
                  className="font-semibold text-emerald-600 hover:text-emerald-700"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="#"
                  className="font-semibold text-emerald-600 hover:text-emerald-700"
                >
                  Privacy Policy
                </a>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary text-white py-4 rounded-xl font-bold text-lg shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Creating Account..." : "Create Account"}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-gray-600">
              Already have an account?{" "}
              <Link
                to="/login"
                className="font-bold text-emerald-600 hover:text-emerald-700"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
